import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { createConnectQueryKey, useMutation, useTransport } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Send, X } from 'lucide-react'
import DOMPurify from 'dompurify'

import {
  sendEmail,
  listInquiryEmails,
  listEmails,
  getEmailThread,
  listEmailThreads,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  SendEmailRequestSchema,
  ListInquiryEmailsRequestSchema,
  ListInquiryEmailsResponseSchema,
} from '@/gen/cortado/v1/cortado_pb'
import { GetEmailThreadRequestSchema, GetEmailThreadResponseSchema } from '@/gen/cortado/v1/emails_pb'
import { useCurrentUser } from '@/stores/user.store'
import { useReplyToThread } from '@/stores/emails.store'

import { Button } from '@/components/ui/button'
import { BetaBadge } from '@/components/shared/beta-badge'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { useRichTextEditor } from '@/hooks/use-rich-text-editor'
import { useEmailTemplates } from '@/stores/email-templates.store'
import { cn } from '@/lib/utils'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { EmailTemplateInsertMenu } from './email-template-insert-menu'
import { DraftConflictAlert, DomainSendDisclosureDialog } from './email-compose-dialogs'
import { appendEmailSignature, isHtmlEmpty, type EmailComposeFormValues } from './email-compose-state'
import { EmailAttachmentsField } from './email-attachments-field'
import { EmailRecipientFields } from './email-recipient-fields'
import { useEmailAttachments } from './use-email-attachments'
import { useEmailDraftSession } from './use-email-draft-session'
import { EMAIL_SENDER_NOT_SHARED_MESSAGE, EmailSenderSelect } from './email-sender-select'
import { useEmailSenderOptions } from './use-email-sender-options'
import { useEmailTemplateInsertion } from './use-email-template-insertion'
import {
  EMAIL_COMPOSE_NEEDS_INBOX_MESSAGE,
  EMAIL_COMPOSE_NO_MAILBOX_MESSAGE,
  EMAIL_COMPOSE_SENDERS_LOAD_ERROR_MESSAGE,
} from '@/lib/email-compose'
import { getTemplateCategory, type EmailComposeSheetProps } from './email-compose-props'
import {
  PORTAL_URL_TEMPLATE_VARIABLE,
  supportsPortalURLTemplateVariable,
  unresolvedComposeTemplateVariables,
} from './email-compose-template-variables'

export type { EmailComposeTemplateContext } from './email-compose-props'

const DOMAIN_SEND_DISCLOSURE_KEY_PREFIX = 'cortado_domain_send_disclosure_acknowledged_v1'

function getDomainSendDisclosureKey(organizationId: string, userId: string) {
  return `${DOMAIN_SEND_DISCLOSURE_KEY_PREFIX}_${organizationId}_${userId}`
}

/**
 * EmailComposeSheet - Sheet component for composing and sending emails
 *
 * Features:
 * - From dropdown showing current user's email accounts
 * - To field (read-only, shows customer's email)
 * - Subject field (editable, pre-populated for replies)
 * - Body textarea for plain text
 * - Handles user with no email account state
 * - Shows loading state while sending
 * - Invalidates email list query on success
 */
export function EmailComposeSheet({
  mode,
  organizationId,
  parentType,
  parentId,
  inquiryId,
  contactId,
  toEmail,
  threadId,
  cameInOnEmail,
  currentUserDraft,
  initialComposeDraft,
  existingThreadSubject,
  templateContext = 'general',
  open,
  onOpenChange,
}: EmailComposeSheetProps) {
  const isReply = mode === 'reply'
  const templateCategory = getTemplateCategory(templateContext)
  const queryClient = useQueryClient()
  const transport = useTransport()
  const { user } = useCurrentUser()
  const [toEmails, setToEmails] = React.useState<string[]>([])
  const [ccEmails, setCcEmails] = React.useState<string[]>([])
  const [bccEmails, setBccEmails] = React.useState<string[]>([])
  const [ccVisible, setCcVisible] = React.useState(false)
  const [bccVisible, setBccVisible] = React.useState(false)
  const [draftVersion, setDraftVersion] = React.useState(0)
  const [domainSendDisclosureOpen, setDomainSendDisclosureOpen] = React.useState(false)
  const [hasAcknowledgedDomainSendDisclosure, setHasAcknowledgedDomainSendDisclosure] = React.useState(true)
  const pendingDomainSendValueRef = React.useRef<EmailComposeFormValues | null>(null)
  const applyingDraftRef = React.useRef(false)
  const userEditedRef = React.useRef(false)
  // Idempotency key for the send currently in flight. Reused across retries
  // of the same submit attempt (double-click, transport retry); cleared once
  // that attempt actually succeeds so the next distinct send gets a fresh
  // one. Mirrors invoices.store.ts/quotes.store.ts's SendInvoice/SendQuote.
  const pendingSendKeyRef = React.useRef<string | null>(null)
  const { replyTo, isLoading: isReplying } = useReplyToThread()
  const { templates, isLoading: templatesLoading } = useEmailTemplates(open ? organizationId : '')
  const rawEmailSignatureHtml = user?.emailSignatureHtml?.trim() ?? ''
  const emailSignatureHtml = isHtmlEmpty(rawEmailSignatureHtml) ? '' : rawEmailSignatureHtml
  const sanitizedEmailSignatureHtml = React.useMemo(() => DOMPurify.sanitize(emailSignatureHtml), [emailSignatureHtml])
  const [includeSignature, setIncludeSignature] = React.useState(Boolean(emailSignatureHtml))
  const {
    senderOptions,
    defaultAccountId,
    hasEmailInboxFeature,
    isLoading: accountsLoading,
    isError: sendersLoadFailed,
  } = useEmailSenderOptions({
    organizationId,
    open,
    userId: user?.id,
  })

  // Send email mutation
  const sendMutation = useMutation(sendEmail, {
    onSuccess: (response) => {
      const updatedThread = response.emailThread
      const inquiryCacheId = inquiryId || (parentType === ParentTypeEnum.PARENT_TYPE_INQUIRY ? parentId : '')

      // Write the response into the read caches first so the new message
      // appears immediately, then invalidate so the server remains the
      // source of truth on the next refetch.
      const inquiryEmailsKey = inquiryCacheId
        ? createConnectQueryKey({
            schema: listInquiryEmails,
            transport,
            input: create(ListInquiryEmailsRequestSchema, {
              inquiryId: inquiryCacheId,
            }),
            cardinality: 'finite',
          })
        : null

      if (updatedThread && inquiryEmailsKey) {
        queryClient.setQueryData(
          inquiryEmailsKey,
          create(ListInquiryEmailsResponseSchema, {
            emailThread: updatedThread,
          }),
        )
      }

      if (updatedThread) {
        const threadDetailKey = createConnectQueryKey({
          schema: getEmailThread,
          transport,
          input: create(GetEmailThreadRequestSchema, { id: updatedThread.id }),
          cardinality: 'finite',
        })
        queryClient.setQueryData(threadDetailKey, create(GetEmailThreadResponseSchema, { thread: updatedThread }))
      }

      if (inquiryEmailsKey) {
        queryClient.invalidateQueries({ queryKey: inquiryEmailsKey })
      }
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmails,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getEmailThread,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreads,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })
  const domainSendDisclosureStorageKey = React.useMemo(() => {
    if (!organizationId || !user?.id) return null
    return getDomainSendDisclosureKey(organizationId, user.id)
  }, [organizationId, user?.id])

  React.useEffect(() => {
    if (!domainSendDisclosureStorageKey) {
      setHasAcknowledgedDomainSendDisclosure(true)
      return
    }

    try {
      setHasAcknowledgedDomainSendDisclosure(localStorage.getItem(domainSendDisclosureStorageKey) === 'true')
    } catch (error) {
      console.warn('Failed to load domain send disclosure state:', error)
      setHasAcknowledgedDomainSendDisclosure(false)
    }
  }, [domainSendDisclosureStorageKey])

  const getSelectedSender = React.useCallback(
    (fromAccountId: string) => senderOptions.find((account) => account.id === fromAccountId),
    [senderOptions],
  )

  async function sendValidatedEmail(value: EmailComposeFormValues) {
    const draftId = draftSession.beginSend()

    const body = includeSignature ? appendEmailSignature(value.body, emailSignatureHtml) : value.body

    const libraryAttachmentIds = libraryAttachments.map((attachment) => attachment.id)

    try {
      if (threadId) {
        await replyTo({
          threadId,
          fromAccountId: value.fromAccountId,
          bodyHtml: body,
          ccEmails,
          bccEmails,
          attachmentIds: attachments.filter((a) => !a.uploading).map((a) => a.id),
          libraryAttachmentIds,
          draftId,
        })
      } else {
        const idempotencyKey = pendingSendKeyRef.current ?? crypto.randomUUID()
        pendingSendKeyRef.current = idempotencyKey

        const request = create(SendEmailRequestSchema, {
          organizationId,
          parentType,
          parentId,
          fromAccountId: value.fromAccountId,
          toEmails,
          ccEmails,
          bccEmails,
          subject: value.subject,
          body,
          attachmentIds: attachments.filter((a) => !a.uploading).map((a) => a.id),
          libraryAttachmentIds,
          draftId,
          idempotencyKey,
        })

        await sendMutation.mutateAsync(request)
      }
    } catch (error) {
      draftSession.cancelSend()
      throw error
    }

    // A later user-initiated send is a new logical operation. Retain the key
    // only across failed or ambiguous attempts of the current one.
    pendingSendKeyRef.current = null
    toast.success('Email sent successfully')
    draftSession.completeSend()
    form.reset()
    setToEmails([])
    setCcEmails([])
    setBccEmails([])
    setCcVisible(false)
    setBccVisible(false)
    resetAttachments()
    onOpenChange(false)
  }

  function handleSendEmailError(error: unknown) {
    console.error('Failed to send email:', error)
    reportError(error, { feature: 'email_send', kind: 'mutation' })
    captureError(error instanceof Error ? error : new Error(String(error)), {
      context: 'email-compose-sheet',
    })
    toast.error('Failed to send email. Please try again.', {
      description: getErrorDescription(error),
    })
  }

  const handleDomainSendDisclosureOpenChange = (nextOpen: boolean) => {
    setDomainSendDisclosureOpen(nextOpen)
    if (!nextOpen) {
      pendingDomainSendValueRef.current = null
    }
  }

  const handleConfirmDomainSendDisclosure = () => {
    const pendingValue = pendingDomainSendValueRef.current
    pendingDomainSendValueRef.current = null

    if (domainSendDisclosureStorageKey) {
      try {
        localStorage.setItem(domainSendDisclosureStorageKey, 'true')
      } catch (error) {
        console.warn('Failed to save domain send disclosure state:', error)
      }
    }

    setHasAcknowledgedDomainSendDisclosure(true)
    setDomainSendDisclosureOpen(false)

    if (pendingValue) {
      void sendValidatedEmail(pendingValue).catch(handleSendEmailError)
    }
  }

  const defaultSubject = React.useMemo(
    () => (isReply && existingThreadSubject ? `Re: ${existingThreadSubject}` : ''),
    [existingThreadSubject, isReply],
  )

  const markDraftEdited = React.useCallback(() => {
    if (applyingDraftRef.current) return
    userEditedRef.current = true
    setDraftVersion((version) => version + 1)
  }, [])

  const {
    attachments,
    setAttachments,
    libraryAttachments,
    setLibraryAttachments,
    libraryPickerOpen,
    setLibraryPickerOpen,
    uploadedBytes,
    combinedCount,
    combinedBytes,
    combinedMeterPercent,
    overCombinedFileLimit,
    overCombinedLimit,
    hasUploading,
    handleFileDrop,
    removeAttachment,
    removeLibraryAttachment,
    cleanupStagedAttachments,
    resetAttachments,
  } = useEmailAttachments({
    organizationId,
    parentType,
    parentId,
    onChange: markDraftEdited,
  })

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      fromAccountId: defaultAccountId,
      subject: defaultSubject,
      body: '',
    },
    onSubmit: async ({ value }) => {
      try {
        // Compose and reply both send from a picked mailbox; the picker
        // preselects the server's default (the user's own, else the org's).
        if (!value.fromAccountId) {
          toast.error('Please select a from email account')
          return
        }
        // A draft can still point at a mailbox that has since stopped being
        // shared with this user; the server would refuse it.
        if (getSelectedSender(value.fromAccountId)?.canSend === false) {
          toast.error(EMAIL_SENDER_NOT_SHARED_MESSAGE)
          return
        }
        if (!isReply && toEmails.length === 0) {
          toast.error('Add at least one recipient')
          return
        }
        if (!value.subject.trim()) {
          toast.error('Subject is required')
          return
        }
        if (isHtmlEmpty(value.body)) {
          toast.error('Email body is required')
          return
        }
        const body = includeSignature ? appendEmailSignature(value.body, emailSignatureHtml) : value.body
        const unresolvedTemplateVariables = unresolvedComposeTemplateVariables(`${value.subject} ${body}`, parentType)
        if (unresolvedTemplateVariables.length > 0) {
          toast.error('Resolve template variables before sending', {
            description: unresolvedTemplateVariables.join(', '),
          })
          return
        }

        // Every sender identity (and every reply) is a mailbox the customer
        // can reply to, so the disclosure applies whenever one is chosen.
        const selectedSender = getSelectedSender(value.fromAccountId)
        const sendsFromDomainAccount = !!selectedSender
        if (sendsFromDomainAccount && !hasAcknowledgedDomainSendDisclosure) {
          pendingDomainSendValueRef.current = value
          setDomainSendDisclosureOpen(true)
          return
        }

        await sendValidatedEmail(value)
      } catch (error) {
        handleSendEmailError(error)
      }
    },
  })

  const deferredTemplateVariableTokens = React.useMemo(
    () =>
      supportsPortalURLTemplateVariable(parentType)
        ? [
            {
              syntax: PORTAL_URL_TEMPLATE_VARIABLE,
              label: 'Portal link',
              parentType: parentType === ParentTypeEnum.PARENT_TYPE_BOOKING ? 'Event' : 'Inquiry',
            },
          ]
        : [],
    [parentType],
  )

  const bodyEditor = useRichTextEditor({
    initialContent: '',
    placeholder: 'Write your message here...',
    minHeight: '200px',
    highlightUnresolvedTemplateVariables: true,
    variableTokens: deferredTemplateVariableTokens,
    links: true,
    onUpdate: (html) => {
      form.setFieldValue('body', html)
      markDraftEdited()
    },
  })

  const getTemplateSubject = React.useCallback(() => form.getFieldValue('subject'), [form])
  const getTemplateBody = React.useCallback(() => form.getFieldValue('body'), [form])
  const setTemplateSubject = React.useCallback((subject: string) => form.setFieldValue('subject', subject), [form])
  const replaceTemplateBody = React.useCallback(
    (body: string) => {
      form.setFieldValue('body', body)
      bodyEditor?.commands.setContent(body)
    },
    [bodyEditor, form],
  )
  const appendTemplateBody = React.useCallback(
    (body: string) => {
      if (bodyEditor) {
        bodyEditor.chain().focus().insertContent(body).run()
        form.setFieldValue('body', bodyEditor.getHTML())
        return
      }
      form.setFieldValue('body', `${form.getFieldValue('body')}${body}`)
    },
    [bodyEditor, form],
  )
  const { handleSelectTemplate, isRendering: isRenderingTemplate } = useEmailTemplateInsertion({
    organizationId,
    inquiryId,
    contactId,
    parentType,
    parentId,
    getSubject: getTemplateSubject,
    getBody: getTemplateBody,
    setSubject: setTemplateSubject,
    replaceBody: replaceTemplateBody,
    appendBody: appendTemplateBody,
    markEdited: markDraftEdited,
  })

  const getDraftFormValues = React.useCallback(
    (): EmailComposeFormValues => ({
      fromAccountId: form.getFieldValue('fromAccountId'),
      subject: form.getFieldValue('subject'),
      body: form.getFieldValue('body'),
    }),
    [form],
  )
  const applyDraftFormValues = React.useCallback(
    (values: { subject: string; body: string; fromAccountId?: string }) => {
      form.setFieldValue('subject', values.subject)
      form.setFieldValue('body', values.body)
      bodyEditor?.commands.setContent(values.body)
      if (values.fromAccountId !== undefined) {
        form.setFieldValue('fromAccountId', values.fromAccountId)
      }
    },
    [bodyEditor, form],
  )
  const getEditorHtml = React.useCallback(() => bodyEditor?.getHTML(), [bodyEditor])
  const setEditorContent = React.useCallback((html: string) => bodyEditor?.commands.setContent(html), [bodyEditor])
  const clearLibraryAttachments = React.useCallback(() => setLibraryAttachments([]), [setLibraryAttachments])
  const setDraftFromAccountId = React.useCallback(
    (accountId: string) => form.setFieldValue('fromAccountId', accountId),
    [form],
  )

  const draftSession = useEmailDraftSession({
    open,
    isReply,
    organizationId,
    parentType,
    parentId,
    threadId,
    toEmail,
    currentUserDraft,
    initialComposeDraft,
    defaultAccountId,
    defaultSubject,
    emailSignatureHtml,
    draftVersion,
    applyingDraftRef,
    userEditedRef,
    toEmails,
    ccEmails,
    bccEmails,
    attachments,
    setToEmails,
    setCcEmails,
    setBccEmails,
    setCcVisible,
    setBccVisible,
    setAttachments,
    setFromAccountId: setDraftFromAccountId,
    clearLibraryAttachments,
    resetAttachments,
    cleanupStagedAttachments,
    setIncludeSignature,
    getFormValues: getDraftFormValues,
    applyFormValues: applyDraftFormValues,
    getEditorHtml,
    setEditorContent,
    onOpenChange,
  })
  const {
    draftStatus,
    hasDraftConflict,
    isDiscardingDraft,
    persistCurrentDraft,
    handleOverwriteDraftConflict,
    handleReloadDraftConflict,
    handleDiscardDraft,
  } = draftSession
  const isPending = sendMutation.isPending || isReplying || isRenderingTemplate || isDiscardingDraft

  React.useEffect(() => {
    if (defaultAccountId && !form.getFieldValue('fromAccountId')) {
      form.setFieldValue('fromAccountId', defaultAccountId)
    }
  }, [defaultAccountId, form])

  React.useEffect(() => {
    bodyEditor?.setEditable(!isPending)
  }, [bodyEditor, isPending])

  const handleSheetOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true)
        return
      }

      if (hasUploading) {
        toast.error('Wait for attachments to finish uploading before closing')
        return
      }

      // Attachments now travel with the autosaved draft (attachment_ids on
      // SaveDraftRequest) — a plain close must not delete them out from under
      // the draft it just persisted. Only an explicit "Discard draft" removes
      // the uploaded files themselves; a plain close only clears local UI state.
      // Skip the save entirely while a conflict is unresolved — it would only
      // fail again against the same stale expectedUpdatedAt, silently, since
      // nobody would see the resulting toast after the sheet closes.
      if (!hasDraftConflict) {
        void persistCurrentDraft()
      }
      resetAttachments()
      onOpenChange(false)
    },
    [hasDraftConflict, hasUploading, onOpenChange, persistCurrentDraft],
  )

  const sendersLoading = accountsLoading
  // Compose and reply send from a connected inbox: the picked mailbox, else
  // the sender's own, else the org default. With none to offer the sheet
  // shows why instead of a form.
  const hasNoEmailAccount =
    !hasEmailInboxFeature || (!sendersLoading && !senderOptions.some((account) => account.canSend))
  const noEmailAccountDescription = !hasEmailInboxFeature
    ? EMAIL_COMPOSE_NEEDS_INBOX_MESSAGE
    : sendersLoadFailed
      ? EMAIL_COMPOSE_SENDERS_LOAD_ERROR_MESSAGE
      : EMAIL_COMPOSE_NO_MAILBOX_MESSAGE

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent className="w-full sm:max-w-[45rem]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {isReply ? 'Reply to Email' : 'New Email'}
            <BetaBadge />
          </SheetTitle>
          <SheetDescription>
            {isReply
              ? 'Send a reply to the customer. Your response will be added to the email thread.'
              : 'Compose a new email to the customer. This will start an email thread.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {hasDraftConflict && (
            <DraftConflictAlert
              onReload={() => void handleReloadDraftConflict()}
              onOverwrite={() => void handleOverwriteDraftConflict()}
            />
          )}
          {hasNoEmailAccount ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{noEmailAccountDescription}</AlertDescription>
            </Alert>
          ) : (
            <form
              id="email-compose-form"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
              className="grid auto-rows-min gap-4"
            >
              {/* From field. A reply may go out from any mailbox the user can
                  send as; the server refuses one that isn't shared with them. */}
              <form.Field name="fromAccountId">
                {(field) => (
                  <EmailSenderSelect
                    value={field.state.value}
                    options={senderOptions}
                    onChange={(value) => field.handleChange(value)}
                    disabled={isPending || sendersLoading}
                    cameInOnEmail={isReply ? cameInOnEmail : undefined}
                  />
                )}
              </form.Field>

              <EmailRecipientFields
                isReply={isReply}
                toEmail={toEmail}
                toEmails={toEmails}
                ccEmails={ccEmails}
                bccEmails={bccEmails}
                ccVisible={ccVisible}
                bccVisible={bccVisible}
                disabled={isPending}
                onToEmailsChange={(next) => {
                  setToEmails(next)
                  markDraftEdited()
                }}
                onCcEmailsChange={(next) => {
                  setCcEmails(next)
                  markDraftEdited()
                }}
                onBccEmailsChange={(next) => {
                  setBccEmails(next)
                  markDraftEdited()
                }}
                onShowCc={() => setCcVisible(true)}
                onShowBcc={() => setBccVisible(true)}
              />

              {/* Subject field */}
              <form.Field name="subject">
                {(field) => (
                  <div className="grid gap-2">
                    <Label htmlFor="subject">
                      Subject <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="subject"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value)
                        markDraftEdited()
                      }}
                      placeholder="Enter email subject"
                      disabled={isPending}
                      className={cn(
                        unresolvedComposeTemplateVariables(field.state.value, parentType).length > 0 &&
                          'border-destructive focus-visible:ring-destructive/30',
                      )}
                    />
                  </div>
                )}
              </form.Field>

              {/* Body field */}
              <form.Field name="body">
                {(field) => (
                  <div className="grid gap-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label>
                        Message <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        {emailSignatureHtml && (
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="include-signature"
                              checked={includeSignature}
                              onCheckedChange={(checked) => setIncludeSignature(checked === true)}
                              disabled={isPending}
                            />
                            <Label htmlFor="include-signature" className="cursor-pointer text-sm font-normal">
                              Include signature
                            </Label>
                          </div>
                        )}
                        <EmailTemplateInsertMenu
                          templates={templates}
                          preferredCategory={templateCategory}
                          isLoading={templatesLoading}
                          disabled={isPending}
                          onSelectTemplate={handleSelectTemplate}
                        />
                      </div>
                    </div>
                    <RichTextEditor
                      editor={bodyEditor}
                      disabled={isPending}
                      minHeight="200px"
                      showLinkButton
                      footer={
                        includeSignature && emailSignatureHtml ? (
                          <div
                            className="mx-3 mt-3 border-t py-3 text-sm text-muted-foreground [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_ul]:ml-5 [&_ul]:list-disc"
                            aria-label="Signature preview"
                            dangerouslySetInnerHTML={{
                              __html: sanitizedEmailSignatureHtml,
                            }}
                          />
                        ) : null
                      }
                    />
                    <form.Subscribe
                      selector={(state) => ({
                        subject: state.values.subject,
                        body: state.values.body,
                      })}
                    >
                      {({ subject, body }) => {
                        const finalBody = includeSignature ? appendEmailSignature(body, emailSignatureHtml) : body
                        const unresolvedTemplateVariables = unresolvedComposeTemplateVariables(`${subject} ${finalBody}`, parentType)

                        if (unresolvedTemplateVariables.length === 0) {
                          return null
                        }

                        return (
                          <Alert variant="destructive" className="py-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                              <span>Resolve these template variables before sending:</span>
                              <span className="flex flex-wrap gap-1 pt-1">
                                {unresolvedTemplateVariables.map((variable) => (
                                  <Badge key={variable} variant="danger" className="font-mono">
                                    {variable}
                                  </Badge>
                                ))}
                              </span>
                            </AlertDescription>
                          </Alert>
                        )
                      }}
                    </form.Subscribe>
                    <input type="hidden" value={field.state.value} readOnly />
                  </div>
                )}
              </form.Field>

              <EmailAttachmentsField
                organizationId={organizationId}
                disabled={isPending}
                attachments={attachments}
                libraryAttachments={libraryAttachments}
                libraryPickerOpen={libraryPickerOpen}
                uploadedBytes={uploadedBytes}
                combinedCount={combinedCount}
                combinedBytes={combinedBytes}
                combinedMeterPercent={combinedMeterPercent}
                overCombinedFileLimit={overCombinedFileLimit}
                overCombinedLimit={overCombinedLimit}
                hasUploading={hasUploading}
                onLibraryPickerOpenChange={setLibraryPickerOpen}
                onLibraryAttachmentsChange={setLibraryAttachments}
                onFileDrop={(files) => void handleFileDrop(files)}
                onRemoveAttachment={removeAttachment}
                onRemoveLibraryAttachment={removeLibraryAttachment}
              />
            </form>
          )}
        </SheetBody>
        <SheetFooter>
          {(isReply || draftStatus !== 'idle') && (
            <div className="mr-auto flex min-h-9 items-center gap-3">
              <Button type="button" variant="ghost" disabled={isPending} onClick={() => void handleDiscardDraft()}>
                <X className="h-4 w-4" />
                Discard draft
              </Button>
              {draftStatus !== 'idle' && (
                <span
                  className={cn(
                    'text-xs',
                    draftStatus === 'error' || draftStatus === 'conflict'
                      ? 'text-destructive'
                      : 'text-muted-foreground',
                  )}
                >
                  {draftStatus === 'saving' && 'Saving...'}
                  {draftStatus === 'saved' && 'Saved'}
                  {draftStatus === 'error' && 'Not saved'}
                  {draftStatus === 'conflict' && 'Conflict — see above'}
                </span>
              )}
            </div>
          )}
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isPending || hasUploading}>
              Cancel
            </Button>
          </SheetClose>
          {hasNoEmailAccount ? (
            <Button type="button" disabled>
              <Send className="h-4 w-4" />
              Send
            </Button>
          ) : (
            <form.Subscribe
              selector={(state) => ({
                fromAccountId: state.values.fromAccountId,
                subject: state.values.subject,
                body: state.values.body,
                isSubmitting: state.isSubmitting,
              })}
            >
              {({ fromAccountId, subject, body, isSubmitting }) => {
                const finalBody = includeSignature ? appendEmailSignature(body, emailSignatureHtml) : body
                const hasUnresolvedTemplateVariables =
                  unresolvedComposeTemplateVariables(`${subject} ${finalBody}`, parentType).length > 0
                const requiredFieldsMissing =
                  !fromAccountId ||
                  getSelectedSender(fromAccountId)?.canSend === false ||
                  !subject.trim() ||
                  isHtmlEmpty(body) ||
                  (!isReply && toEmails.length === 0)

                return (
                  <Button
                    type="submit"
                    form="email-compose-form"
                    disabled={
                      isPending ||
                      isSubmitting ||
                      hasNoEmailAccount ||
                      hasUnresolvedTemplateVariables ||
                      requiredFieldsMissing ||
                      hasUploading ||
                      overCombinedLimit
                    }
                  >
                    {isPending ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send
                      </>
                    )}
                  </Button>
                )
              }}
            </form.Subscribe>
          )}
        </SheetFooter>
      </SheetContent>
      <DomainSendDisclosureDialog
        open={domainSendDisclosureOpen}
        disabled={isPending}
        onOpenChange={handleDomainSendDisclosureOpenChange}
        onConfirm={handleConfirmDomainSendDisclosure}
      />
    </Sheet>
  )
}
